import { assignmentRiskCopy, assignmentRiskHref } from './assignment-risk';
import { UNFILLED_ALERT_HORIZON_HOURS } from '@statura/utils';
/**
 * PHI-free notification templates per workflow type.
 *
 * Spec §10.3 — push titles/bodies surface on lock screens, so they
 * MUST contain no client names, room numbers, clinical detail, or
 * staff personal information beyond first name in a work context.
 *
 * Facility name is NOT PHI (per spec) and is permitted in all
 * channels including push and email.
 */
import type { WorkflowType } from './types';
import type { ClientRecordTabId } from '@/lib/clients/resolve-initial-client-tab';
import { sahServiceStructuralDriftHref } from '@/lib/clients/sah-service-structural-drift';
import { PATH_ROLE_ACCESS } from '@/lib/route-access';
import { sahClientOnboardingPackHref } from '@/lib/sah/sah-href-contract';

/** Manager cohort for leave approval notifications — PATH_ROLE_ACCESS['/roster/leave']. */
const LEAVE_MANAGER_ROLES = new Set(PATH_ROLE_ACCESS['/roster/leave'] ?? []);

export function isLeaveRequestRequesterAudience(
  metadata: Record<string, unknown> | undefined,
  recipientRole: string | null | undefined,
): boolean {
  if (metadata?.decision_back_to_requester === true) return true;
  if (!recipientRole) return false;
  return !LEAVE_MANAGER_ROLES.has(recipientRole);
}

export function leaveRequestResourceUrl(
  sourceId: string,
  metadata: Record<string, unknown> | undefined,
  recipientRole?: string | null,
): string {
  if (isLeaveRequestRequesterAudience(metadata, recipientRole ?? null)) {
    return `/me/leave?request=${encoded(sourceId)}`;
  }
  return `/roster/leave?request_id=${encoded(sourceId)}`;
}

export function leaveRequestNotificationBody(
  metadata: Record<string, unknown> | undefined,
  recipientRole?: string | null,
): string {
  if (isLeaveRequestRequesterAudience(metadata, recipientRole ?? null)) {
    return 'Your leave request has a decision.';
  }
  return 'A leave request requires your approval.';
}

export function peopleRenewalResourceUrl(
  sourceTable: string,
  metadata: Record<string, unknown>,
  role?: string | null,
): string {
  if (metadata.audience === 'manager') {
    return ['OWNER', 'CEO', 'OPERATIONS_DIRECTOR', 'HR_DIRECTOR'].includes(role ?? '')
      ? '/people/learning?view=renewals'
      : '/people/compliance?view=renewals';
  }
  return sourceTable === 'training_assignments' ? '/me/training' : '/me/compliance';
}

export interface RenderedNotification {
  title: string;
  body: string;
  /** Deep link path within the app, used to construct app_url for emails. */
  resourceUrl: string;
  /** Lock-screen-safe push text when it must differ from the full body. */
  pushBody?: string;
}

export interface TemplateContext {
  workflowType: WorkflowType;
  facilityName?: string | null;
  metadata?: Record<string, unknown>;
  escalationTier?: number;
  /** Pre-formatted "14 hours 22 minutes remaining" or "4 days remaining". */
  deadlineRemaining?: string;
}

function isWorkerScreeningRequest(metadata?: Record<string, unknown>): boolean {
  return metadata?.alert_type === 'WORKER_SCREENING_REQUESTED';
}

function metadataString(metadata: Record<string, unknown> | undefined, key: string): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function metadataText(metadata: Record<string, unknown> | undefined, key: string): string | null {
  const value = metadata?.[key];
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function encoded(value: string): string {
  return encodeURIComponent(value);
}

/** No navigable in-app recovery page. Inbox and dispatch must not render a link. */
export const AUDIT_ONLY_UNAVAILABLE_RESOURCE_URL = '';

function unavailableNotificationRoute(sourceId: string): string {
  return `/settings/notification-audit?event=${encoded(sourceId)}`;
}

function routeForZeroRecipientFallback(metadata?: Record<string, unknown>): string {
  const workflowType = metadataString(metadata, 'original_workflow_type');
  return workflowType
    ? `/settings/notification-routing?workflow=${encoded(workflowType)}`
    : '/settings/notification-routing';
}

function routeForRosterShiftAlert(sourceId: string, metadata?: Record<string, unknown>): string {
  const openShiftId = metadataString(metadata, 'open_shift_id');
  if (openShiftId) return `/roster/open-shifts/${encoded(openShiftId)}`;

  const rosterPeriodId = metadataString(metadata, 'roster_period_id');
  if (rosterPeriodId) {
    return `/roster/dispatch?period=${encoded(rosterPeriodId)}&view=week#shift-${encoded(sourceId)}`;
  }

  return `/roster/replace/${encoded(sourceId)}`;
}

function routeForRosterAssignmentAlert(
  sourceId: string,
  metadata?: Record<string, unknown>,
): string {
  const assignmentId = metadataString(metadata, 'assignment_id');
  const shiftId = metadataString(metadata, 'shift_id');
  const slotId = metadataString(metadata, 'slot_id');

  // source_id is the authoritative assignment PK for this producer. Reject a
  // contradictory metadata copy instead of opening some other assignment.
  if ((assignmentId && assignmentId !== sourceId) || !shiftId) {
    return unavailableNotificationRoute(sourceId);
  }

  const slotQuery = slotId ? `&slotId=${encoded(slotId)}` : '';
  return `/roster/replace/${encoded(shiftId)}?assignmentId=${encoded(sourceId)}${slotQuery}`;
}

function routeForRosterNoShow(
  sourceTable: string,
  sourceId: string,
  metadata?: Record<string, unknown>,
): string {
  if (sourceTable !== 'roster_runtime_status') {
    return unavailableNotificationRoute(sourceId);
  }

  const runtimeStatusId = metadataString(metadata, 'runtime_status_id');
  if (runtimeStatusId && runtimeStatusId !== sourceId) {
    return unavailableNotificationRoute(sourceId);
  }

  const model = metadataString(metadata, 'roster_model');
  if (model === 'SAH') {
    // ONE DOOR (2026-08-18): the visit-exception queue lives on its own page;
    // the legacy tab/focus keys stay understood by parseTodayRouteState there.
    return `/roster/visit-exceptions?section=visit-exceptions&tab=home-care&focus=${encoded(sourceId)}`;
  }
  if (model !== 'RAC') return unavailableNotificationRoute(sourceId);

  const periodId = metadataString(metadata, 'roster_period_id');
  const facilityId = metadataString(metadata, 'facility_id');
  const shiftId = metadataString(metadata, 'shift_id');
  const slotId = metadataString(metadata, 'slot_id');
  const shiftDate = metadataString(metadata, 'shift_date');
  if (!periodId || !facilityId || !shiftId || !slotId || !shiftDate) {
    return unavailableNotificationRoute(sourceId);
  }

  return (
    `/roster/dispatch?period=${encoded(periodId)}&view=day&date=${encoded(shiftDate)}` +
    `&facility=${encoded(facilityId)}&shift=${encoded(shiftId)}&slotId=${encoded(slotId)}` +
    `#shift-${encoded(slotId)}`
  );
}

function isWorkerCertificationAlert(
  sourceTable: string,
  metadata?: Record<string, unknown>,
): boolean {
  return (
    sourceTable === 'worker_certifications' ||
    metadata?.source_record_type === 'worker_certification' ||
    typeof metadata?.worker_certification_id === 'string'
  );
}

function routeForWorkforceCompliance(
  sourceTable: string,
  sourceId: string,
  metadata?: Record<string, unknown>,
): string {
  if (
    metadata?.alert_type === 'WORKER_ASSIGNMENT_AT_RISK' ||
    metadata?.event_type === 'WORKER_ASSIGNMENT_AT_RISK'
  ) {
    return assignmentRiskHref(metadata);
  }
  // Worker-audience companion events (screening expiry, visa expiry) must land
  // on the worker's own renewal surface — the manager routes below
  // (/people/compliance/*, /people/workers/*) are role-gated and dead-end for a worker.
  if (metadata?.audience === 'worker') {
    return '/me/renewals';
  }
  if (sourceTable === 'worker_screenings' || isWorkerScreeningRequest(metadata)) {
    return `/people/compliance/screening/${encoded(sourceId)}`;
  }
  if (isWorkerCertificationAlert(sourceTable, metadata)) {
    const staffId = metadataString(metadata, 'staff_id');
    return staffId
      ? `/people/compliance/certifications/${encoded(staffId)}?focus=${encoded(sourceId)}`
      : `/people/compliance/certifications?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'worker_probation_milestones') {
    return `/people/development/performance?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'clinical_supervision_events') {
    return `/people/development/supervision?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'staff') {
    if (metadata?.alert_type === 'SCIM_WORKER_NEEDS_ONBOARDING') {
      return '/people/onboarding?view=not-started';
    }
    return `/people/workers/${encoded(sourceId)}`;
  }
  if (sourceTable === 'worker_onboarding_journeys') {
    if (
      metadata?.alert_type === 'ONBOARDING_DMS_BUNDLE_UNAVAILABLE' ||
      metadata?.alert_type === 'ONBOARDING_EXTERNAL_EVIDENCE_EXPIRED'
    ) {
      return `/people/onboarding/${encoded(sourceId)}`;
    }
    const staffId = metadataString(metadata, 'staff_id');
    return staffId ? `/people/workers/${encoded(staffId)}` : '/people/onboarding';
  }
  if (sourceTable === 'worker_onboarding_documents') {
    if (metadata?.alert_type === 'ONBOARDING_DOCUMENT_SIGNED') {
      return `/people/onboarding/documents?focus=${encoded(sourceId)}`;
    }
    const staffId = metadataString(metadata, 'staff_id');
    return staffId ? `/people/workers/${encoded(staffId)}` : '/people/compliance';
  }
  if (sourceTable === 'compliance_renewal_bookings') {
    const staffId = metadataString(metadata, 'staff_id');
    return staffId
      ? `/people/compliance/certifications/${encoded(staffId)}`
      : `/people/compliance/certifications`;
  }
  return '/people/compliance';
}

function renderWorkforceComplianceNotification(
  sourceTable: string,
  sourceId: string,
  facility: string,
  ctx: TemplateContext,
): RenderedNotification {
  const url = deepLinkFor('WORKFORCE_COMPLIANCE', sourceTable, sourceId, ctx.metadata);

  if (
    ctx.metadata?.alert_type === 'WORKER_ASSIGNMENT_AT_RISK' ||
    ctx.metadata?.event_type === 'WORKER_ASSIGNMENT_AT_RISK'
  ) {
    return { ...assignmentRiskCopy(ctx.metadata), resourceUrl: url };
  }

  if (sourceTable === 'onboarding_nudge_intents') {
    const nudge = ctx.metadata?.onboarding_nudge_context as
      | {
          kind?: string;
          summary_count?: number;
          summary_worker_owned?: boolean;
          summary_reassigned?: boolean;
        }
      | undefined;
    if (nudge?.kind === 'REASSIGNMENT_SUMMARY') {
      const count = nudge.summary_count;
      if (!Number.isInteger(count) || !count || count < 1) {
        throw new Error('onboarding_nudge_summary_count_missing');
      }
      const tasks = `${count} overdue onboarding task${count === 1 ? '' : 's'}`;
      const resourceUrl = nudge.summary_worker_owned
        ? '/me/onboarding'
        : '/people/onboarding?view=assigned-to-me';
      // "You now own" only for a real reassignment of ownership to this person;
      // first-sight overdue work and follow-up-only recipients get neutral copy.
      return nudge.summary_reassigned === true
        ? {
            title: 'Overdue onboarding work assigned to you',
            body: `You now own ${tasks}. Open your assigned work queue to review them.`,
            resourceUrl,
          }
        : {
            title: 'Overdue onboarding work needs you',
            body: `${tasks} ${count === 1 ? 'needs' : 'need'} your follow-up. Open your assigned work queue to review them.`,
            resourceUrl,
          };
    }
    return {
      title:
        ctx.metadata?.kind === 'ESCALATION'
          ? 'Overdue onboarding follow-up'
          : 'Onboarding reminder',
      body:
        ctx.metadata?.kind === 'ESCALATION'
          ? 'An overdue onboarding step needs owner follow-up.'
          : 'An onboarding step is due for follow-up.',
      resourceUrl: url,
    };
  }

  if (ctx.metadata?.alert_type === 'ONBOARDING_DOCUMENT_SIGNED') {
    return {
      title: 'Onboarding document signed',
      body: `A signed onboarding document is ready for provider follow-up${facility}.`,
      resourceUrl: url,
    };
  }

  if (ctx.metadata?.alert_type === 'ONBOARDING_DMS_BUNDLE_UNAVAILABLE') {
    const dmsState = metadataString(ctx.metadata, 'dms_state')?.toLowerCase() ?? 'unavailable';
    return {
      title: `Onboarding DMS evidence ${dmsState}`,
      body: `DMS evidence mapped to a worker onboarding requirement is no longer available. Review the onboarding journey and provide or select replacement evidence${facility}.`,
      resourceUrl: url,
    };
  }

  if (ctx.metadata?.alert_type === 'ONBOARDING_EXTERNAL_EVIDENCE_EXPIRED') {
    return {
      title: 'External onboarding evidence expired',
      body: `External evidence for a worker onboarding step has expired. Re-attest or record replacement evidence before the step is approved${facility}.`,
      resourceUrl: url,
    };
  }

  if (ctx.metadata?.alert_type === 'SCIM_WORKER_NEEDS_ONBOARDING') {
    const workerEmail = metadataString(ctx.metadata, 'worker_email');
    return {
      title: 'SCIM worker needs onboarding',
      body: workerEmail
        ? `SCIM provisioned ${workerEmail} without an onboarding journey. Start onboarding before rostering.`
        : 'A SCIM-provisioned worker needs onboarding before rostering.',
      resourceUrl: url,
    };
  }
  if (ctx.metadata?.alert_type === 'WORKER_ONBOARDING_STARTED') {
    return {
      title: 'Onboarding journey started',
      body: `A worker onboarding journey has started${facility}.`,
      resourceUrl: url,
    };
  }
  if (
    ctx.metadata?.alert_type === 'PROBATION_MILESTONE_DUE' ||
    ctx.metadata?.alert_type === 'PROBATION_MILESTONE_OVERDUE'
  ) {
    const overdue = ctx.metadata.alert_type === 'PROBATION_MILESTONE_OVERDUE';
    const milestone = metadataText(ctx.metadata, 'milestone');
    const dueDate = metadataString(ctx.metadata, 'due_date');
    const reviewLabel = milestone ? `${milestone}-day probation review` : 'Probation review';
    return {
      title: overdue ? 'Probation review overdue' : 'Probation review due',
      body: overdue
        ? `${reviewLabel} was due${dueDate ? ` on ${dueDate}` : ''}. Record the review outcome.`
        : `${reviewLabel} is due${dueDate ? ` on ${dueDate}` : ''}. Record the review outcome.`,
      resourceUrl: url,
    };
  }
  if (
    ctx.metadata?.alert_type === 'WORKER_VISA_EXPIRING' ||
    ctx.metadata?.alert_type === 'WORKER_VISA_EXPIRED'
  ) {
    const expired = ctx.metadata.alert_type === 'WORKER_VISA_EXPIRED';
    if (ctx.metadata?.audience === 'worker') {
      return {
        title: expired ? 'Your work visa has expired' : 'Your work visa is expiring',
        body: expired
          ? 'Your work-rights document has expired. Upload your renewed evidence as soon as possible.'
          : 'Your work-rights document is expiring soon. Upload your renewed evidence.',
        resourceUrl: url,
      };
    }
    return {
      title: expired ? 'Work visa expired' : 'Work visa expiring',
      body: `A worker work-rights document requires compliance review${facility}.`,
      resourceUrl: url,
    };
  }
  if (ctx.metadata?.alert_type === 'WORKER_SCREENING_EXPIRING') {
    const screeningType = metadataString(ctx.metadata, 'screening_type');
    const daysRemaining = metadataText(ctx.metadata, 'days_remaining');
    const typeLabel = screeningType
      ? screeningType.replaceAll('_', ' ').toLowerCase()
      : 'screening';
    const window = daysRemaining ? ` in ${daysRemaining} days` : ' soon';
    if (ctx.metadata?.audience === 'worker') {
      return {
        title: 'Your screening is expiring',
        body: `Your ${typeLabel} expires${window}. Book or upload your renewal.`,
        resourceUrl: url,
      };
    }
    return {
      title: 'Worker screening expiring',
      body: `A worker ${typeLabel} expires${window}${facility}. Arrange the renewal before it lapses.`,
      resourceUrl: url,
    };
  }
  if (isWorkerScreeningRequest(ctx.metadata)) {
    return {
      title: 'Worker Screening Review',
      body: `A worker screening request requires manual verification${facility}.`,
      resourceUrl: url,
    };
  }
  if (isWorkerCertificationAlert(sourceTable, ctx.metadata)) {
    return {
      title: 'Worker Certification Review',
      body: `A worker certification requires compliance review${facility}.`,
      resourceUrl: url,
    };
  }
  return {
    title: 'Workforce Compliance Alert',
    body: `A workforce compliance event requires review${facility}.`,
    resourceUrl: url,
  };
}

function clientWorkspaceRoute(
  metadata: Record<string, unknown> | undefined,
  tab: ClientRecordTabId,
  fallback: string,
): string {
  const clientId = metadataString(metadata, 'client_id');
  return clientId ? `/clients/${encoded(clientId)}?tab=${encoded(tab)}` : fallback;
}

function sahOnboardingRoute(metadata?: Record<string, unknown>): string {
  const clientId = metadataString(metadata, 'client_id');
  return clientId ? sahClientOnboardingPackHref(clientId) : '/home-care/readiness';
}

function routeForSystemAlert(
  sourceTable: string,
  sourceId: string,
  metadata?: Record<string, unknown>,
): string {
  if (sourceTable === 'notification_policy_revisions') {
    // A policy revision is not a notification event; the editor has no revision deep link.
    return '/settings/notification-routing';
  }
  if (metadata?.kind === 'SAH_SERVICE_STRUCTURAL_DRIFT') {
    return sahServiceStructuralDriftHref(
      metadataString(metadata, 'client_id') ?? metadataString(metadata, 'clientId'),
    );
  }
  if (sourceTable === 'workflow_tasks') return `/tasks/${encoded(sourceId)}`;
  if (sourceTable === 'roster_shifts') return routeForRosterShiftAlert(sourceId, metadata);
  if (sourceTable === 'open_shifts') return `/roster/open-shifts/${encoded(sourceId)}`;
  if (sourceTable === 'shift_offers') {
    const openShiftId = metadataString(metadata, 'open_shift_id');
    return openShiftId ? `/roster/open-shifts/${encoded(openShiftId)}` : '/roster/open-shifts';
  }
  if (sourceTable === 'care_plans') return `/care-plans/${encoded(sourceId)}`;
  if (sourceTable === 'assessments') return `/assessments/${encoded(sourceId)}`;
  if (sourceTable === 'clinical_alerts') return `/clinical/alerts/${encoded(sourceId)}`;
  if (sourceTable === 'frontline_escalations') return `/clinical/escalations/${encoded(sourceId)}`;
  if (sourceTable === 'medication_incidents') {
    return `/clinical/medication-incidents/${encoded(sourceId)}`;
  }
  if (sourceTable === 'wound_assessments') {
    const woundId = metadataString(metadata, 'wound_id');
    return woundId
      ? `/clinical/wounds/${encoded(woundId)}`
      : `/clinical/wounds?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'incidents') return `/compliance/incidents/${encoded(sourceId)}`;
  if (sourceTable === 'complaints') return `/compliance/complaints/${encoded(sourceId)}`;
  if (sourceTable === 'consumer_feedback') {
    return `/compliance/complaints?tab=feedback&focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'data_breaches') return `/compliance/data-breach/${encoded(sourceId)}`;
  if (sourceTable === 'restrictive_practices') {
    return `/compliance/restrictive-practices/${encoded(sourceId)}`;
  }
  if (sourceTable === 'restrictive_practice_episodes') {
    const practiceId = metadataString(metadata, 'restrictive_practice_id');
    return practiceId
      ? `/compliance/restrictive-practices/${encoded(practiceId)}`
      : '/compliance/restrictive-practices';
  }
  if (sourceTable === 'service_agreements') {
    const clientId = metadataString(metadata, 'client_id');
    return clientId
      ? `/clients/${encoded(clientId)}/care-plans/service-agreements/${encoded(sourceId)}/review`
      : '/pricing/agreements';
  }
  if (sourceTable === 'consents') {
    return clientWorkspaceRoute(metadata, 'documents', '/settings/onboarding/consents');
  }
  if (sourceTable === 'invoices') return `/finance/invoices/${encoded(sourceId)}`;
  if (sourceTable === 'sah_monthly_statements') {
    return `/home-care/statements?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'sah_claim_batches') return `/home-care/claims?focus=${encoded(sourceId)}`;
  if (sourceTable === 'sah_quarter_budgets') {
    return clientWorkspaceRoute(
      metadata,
      'budget',
      `/home-care/budgets?focus=${encoded(sourceId)}`,
    );
  }
  if (sourceTable === 'home_assessments') {
    return `/home-care/assessments/${encoded(sourceId)}`;
  }
  if (sourceTable === 'clients') return `/clients/${encoded(sourceId)}`;
  if (sourceTable === 'residential_round_assignments') {
    return clientWorkspaceRoute(metadata, 'rac-hub', '/residential/daily-living');
  }
  if (sourceTable === 'resident_daily_care_escalations') {
    return clientWorkspaceRoute(metadata, 'rac-hub', '/residential/daily-living');
  }
  if (sourceTable === 'residential_respite_episodes') {
    return clientWorkspaceRoute(metadata, 'rac-hub', '/residential/admissions');
  }
  if (sourceTable === 'services_delivered') {
    return clientWorkspaceRoute(
      metadata,
      'services',
      `/home-care/service-delivery?focus=${encoded(sourceId)}`,
    );
  }
  if (sourceTable === 'contractor_evidence_submissions') {
    return `/contractors/evidence/queue/${encoded(sourceId)}`;
  }
  if (sourceTable === 'timesheets') return `/roster/timesheets?timesheet_id=${encoded(sourceId)}`;
  if (sourceTable === 'roster_shift_assignments') {
    return routeForRosterAssignmentAlert(sourceId, metadata);
  }
  if (sourceTable === 'quality_indicator_records' || sourceTable === 'qi_collection_periods') {
    return `/compliance/quality-indicators?tab=quarter&focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'iot_alerts') return `/dashboards/iot?focus=${encoded(sourceId)}`;
  if (sourceTable === 'whistleblower_reports') {
    return `/compliance/whistleblower?tab=case&caseId=${encoded(sourceId)}`;
  }
  if (sourceTable === 'training_assignments') return `/people/learning?focus=${encoded(sourceId)}`;
  if (sourceTable === 'worker_onboarding_documents') {
    const staffId = metadataString(metadata, 'staff_id');
    return staffId ? `/people/workers/${encoded(staffId)}` : '/people/onboarding';
  }
  if (sourceTable === 'workplace_policies') {
    return `/people/policies-conduct/publications?focus=${encoded(sourceId)}`;
  }
  if (sourceTable === 'staff_policy_requirements') {
    return `/people/policies-conduct/assignments?focus=${encoded(sourceId)}`;
  }
  return `/settings/notification-audit?event=${encoded(sourceId)}`;
}

function deepLinkFor(
  workflowType: WorkflowType,
  sourceTable: string,
  sourceId: string,
  metadata?: Record<string, unknown>,
): string {
  // Paths verified against live routes. Where a dedicated detail page doesn't
  // exist yet, we use `?focus=<id>` against the list view so the user at least
  // lands on a live page that can scroll/filter to the row. Sprints 2-3 ship
  // the actual detail pages and this file is updated to drop the ?focus=
  // fallback for each workflow as its page lands.
  switch (workflowType) {
    case 'DATA_BREACH':
      return `/compliance/data-breach/${encoded(sourceId)}`;
    case 'IPC_OUTBREAK':
      return `/compliance/ipc/outbreaks?focus=${encoded(sourceId)}`;
    case 'SCREENING_EXPIRY':
      return `/people/compliance/screening?focus=${encoded(sourceId)}`;
    case 'SIRS_INCIDENT':
    case 'SIRS_SCOPE_REVIEW_REQUIRED':
    case 'NON_SIRS_INCIDENT':
    case 'SIRS_INVESTIGATION_REPORT': {
      // RP cross-trigger now creates a real incidents row (migration
      // 20260417140001) so the default path lands on /compliance/incidents.
      // The RP-source fallback below is kept for:
      //   - legacy notification_events rows with source_table =
      //     'restrictive_practice_episodes' emitted before 2026-04-17
      //   - medication_incidents cross-trigger which still does not
      //     auto-create an incidents row
      if (sourceTable === 'restrictive_practice_episodes') {
        const practiceId = metadata?.restrictive_practice_id as string | undefined;
        return practiceId
          ? `/compliance/restrictive-practices/${practiceId}`
          : `/compliance/restrictive-practices`;
      }
      if (sourceTable === 'medication_incidents') {
        return `/clinical/medication-incidents/${sourceId}`;
      }
      return `/compliance/incidents/${sourceId}`;
    }
    case 'OPEN_DISCLOSURE':
      // Sprint 4.1 — dedicated execution page ships with this delivery
      return `/compliance/incidents/${sourceId}?tab=open-disclosure`;
    case 'RESTRICTIVE_PRACTICE_USE': {
      // The RP trigger emits source_id = restrictive_practice_episodes.id
      // but the detail page loads from restrictive_practices. Use
      // metadata.restrictive_practice_id to route to the parent record.
      const practiceId = metadata?.restrictive_practice_id as string | undefined;
      return practiceId
        ? `/compliance/restrictive-practices/${practiceId}`
        : `/compliance/restrictive-practices`;
    }
    case 'ESCALATION':
      return `/clinical/escalations/${sourceId}`;
    case 'WOUND_DETERIORATION': {
      // The trigger stores `wound_assessments.id` as `source_id` but puts
      // the parent `wound_id` on `metadata` (see migration
      // 20260515000007_workflow_routing_phase2_triggers.sql `trg_notify_wound_deterioration`).
      // Deep-link directly to the wound detail page so recipients land on
      // something actionable (mark healed, review assessments). Fall back
      // to the list if metadata is missing.
      const woundId = metadata?.wound_id as string | undefined;
      return woundId ? `/clinical/wounds/${woundId}` : `/clinical/wounds?focus=${sourceId}`;
    }
    case 'CLINICAL_ALERT':
      if (sourceTable === 'clinical_alerts') return `/clinical/alerts/${encoded(sourceId)}`;
      if (sourceTable === 'staff') return `/people/workers/${encoded(sourceId)}?tab=compliance`;
      if (sourceTable === 'care_plans') return `/care-plans/${encoded(sourceId)}`;
      if (sourceTable === 'assessments') return `/assessments/${encoded(sourceId)}`;
      if (sourceTable === 'qi_collection_periods') {
        return `/compliance/quality-indicators?tab=quarter&focus=${encoded(sourceId)}`;
      }
      return `/clinical/alerts/${encoded(sourceId)}`;
    case 'MEDICATION_INCIDENT':
      if (sourceTable === 'medication_administrations') {
        return `/medications/mar?focus=${encoded(sourceId)}`;
      }
      return `/clinical/medication-incidents/${encoded(sourceId)}`;
    case 'WHS_INCIDENT':
      // Actual WHS detail page is under /compliance/whs/incidents/[id]
      return `/compliance/whs/incidents/${sourceId}`;
    case 'SHIFT_HANDOVER':
      // No detail page — the list consumes one exact, tenant-scoped handover.
      return `/roster/handovers?handoverId=${encoded(sourceId)}`;
    case 'LEAVE_REQUEST':
      return leaveRequestResourceUrl(sourceId, metadata);
    case 'ABSENCE_LEAVE_REQUIRED': {
      const episodeId = metadataString(metadata, 'absence_episode_id') ?? sourceId;
      return `/me/leave?from_absence=${encoded(episodeId)}`;
    }
    case 'LEAVE_EVIDENCE_DUE':
      return `/me/leave?request=${encoded(sourceId)}`;
    case 'SHIFT_SWAP_REQUEST':
      // Directory is shift-swaps (not swaps). The list consumes one exact request.
      return `/roster/shift-swaps?requestId=${encoded(sourceId)}`;
    case 'TASK_COMPLETION':
      if (sourceTable === 'workflow_tasks') return `/tasks/${sourceId}`;
      return `/tasks?source=${encoded(sourceTable)}&focus=${encoded(sourceId)}`;
    case 'UNEXPLAINED_ABSENCE':
      return `/compliance/incidents/${sourceId}`;
    case 'ROSTER_RUNTIME_SHIFT_NO_SHOW': {
      return routeForRosterNoShow(sourceTable, sourceId, metadata);
    }
    case 'ROSTER_RUNTIME_SHIFT_AWAITING_CLOCK': {
      const focusId =
        metadataString(metadata, 'runtime_status_id') ??
        metadataString(metadata, 'shift_id') ??
        sourceId;
      return `/roster/visit-exceptions?section=visit-exceptions&tab=home-care&focus=${encoded(focusId)}`;
    }
    case 'ROSTER_RUNTIME_MISSED_CLOCK_OUT': {
      // Auto-close/sweep events source roster_runtime_status; the materialised
      // timesheet id (when present) deep-links straight to the review row.
      const timesheetId = metadataString(metadata, 'timesheet_id');
      return timesheetId
        ? `/roster/timesheets?timesheet_id=${encoded(timesheetId)}`
        : // Daily time review relocated to /roster/time-review (ONE DOOR
          // 2026-08-18); link the live page, not the retired bookmark alias.
          '/roster/time-review';
    }
    case 'TIMESHEET_REJECTED':
      // Recipient is the affected worker (named recipient) — their own list.
      return `/me/timesheets?focus=${encoded(sourceId)}`;
    case 'WORKFORCE_WORKED_TIME_PARTIALLY_APPROVED':
      return `/me/timesheets?focus=${encoded(sourceId)}`;
    case 'ESCALATION_EXHAUSTED':
      // Sprint 4.6 — keep the audit deep-link but cross-reference the
      // parent event id so admins can see the original workflow context
      return `/settings/notification-audit?event=${encoded(sourceId)}`;
    case 'WORKFORCE_COMPLIANCE':
      return routeForWorkforceCompliance(sourceTable, sourceId, metadata);
    case 'WORKFORCE_POLICY_ASSIGNED':
    case 'WORKFORCE_POLICY_REMINDER': {
      const actionUrl = metadataString(metadata, 'action_url');
      return actionUrl && actionUrl.startsWith('/me/policies/')
        ? actionUrl
        : `/me/policies/${encoded(metadataString(metadata, 'policy_id') ?? sourceId)}`;
    }
    case 'WORKFORCE_REPLACE_ON_SICK_DAY': {
      const openShiftId = metadataString(metadata, 'open_shift_id');
      return openShiftId ? `/roster/open-shifts/${encoded(openShiftId)}` : '/roster/open-shifts';
    }
    case 'MESSAGE':
      return clientWorkspaceRoute(metadata, 'family', '/settings/notifications/log');
    case 'RAC_ADMISSION':
    case 'RAC_DISCHARGE':
      return clientWorkspaceRoute(metadata, 'overview', '/residential/admissions');
    case 'RAC_RESPITE': {
      const trigger = metadataString(metadata, 'workflow_trigger_event');
      return clientWorkspaceRoute(
        metadata,
        trigger === 'rac.respite_billable_days_generated' ? 'budget' : 'rac-hub',
        '/residential/admissions',
      );
    }
    case 'SAH_ONBOARDING':
      return sahOnboardingRoute(metadata);
    case 'COMPLAINT':
      return `/compliance/complaints/${encoded(sourceId)}`;
    case 'CARE_PLAN_REVIEW': {
      const carePlanId = metadataString(metadata, 'care_plan_id') ?? sourceId;
      return `/care-plans/${encoded(carePlanId)}`;
    }
    case 'REGULATOR_SUBMISSION_REMINDER':
      if (sourceTable === 'data_breaches') return `/compliance/data-breach/${encoded(sourceId)}`;
      if (sourceTable === 'qi_collection_periods') {
        return `/compliance/quality-indicators?tab=quarter&focus=${encoded(sourceId)}`;
      }
      return `/compliance/incidents/${encoded(sourceId)}`;
    // Path-to-GO S3 — NO_SHOW only (LATE deliberately omitted; welfare wires on no-show).
    case 'SAH_BUDGET_VARIANCE':
      return clientWorkspaceRoute(
        metadata,
        'budget',
        `/home-care/budgets?focus=${encoded(sourceId)}`,
      );
    case 'OPS_CARE_MINUTES_SHORTFALL':
      return `/residential/care-minutes?focus=${encoded(sourceId)}`;
    case 'OPS_RN_COVERAGE_MID_PERIOD_GAP':
      // No executable producer currently supplies the required period,
      // facility and cause identity, and no recipient-accessible recovery
      // page exists. Keep external delivery blocked and do not emit the
      // admin-only notification-audit URL as a dead in-app link.
      return AUDIT_ONLY_UNAVAILABLE_RESOURCE_URL;
    case 'OPS_RP_AUTHORISATION_LAPSING':
      return `/compliance/restrictive-practices/${encoded(sourceId)}`;
    case 'OPS_RESIDENT_CLINICAL_ALERT':
      return `/clinical/alerts/${encoded(sourceId)}`;
    case 'SYSTEM_ALERT':
      if (metadata?.alert_type === 'PEOPLE_RENEWAL') {
        return peopleRenewalResourceUrl(sourceTable, metadata);
      }
      if (metadata?.alert_type === 'CHSP_CARE_PLAN_REVIEW_REQUEST_PENDING') {
        const clientId = metadataString(metadata, 'client_id');
        return clientId
          ? `/clients/${encoded(clientId)}?tab=care-plans&reviewRequestId=${encoded(sourceId)}`
          : AUDIT_ONLY_UNAVAILABLE_RESOURCE_URL;
      }
      if (metadata?.alert_type === 'CHSP_CLIENT_REQUEST_PENDING') {
        return `/home-care/service-delivery?requestId=${encoded(sourceId)}`;
      }
      if (metadata?.kind === 'SAH_SERVICE_STRUCTURAL_DRIFT') {
        return sahServiceStructuralDriftHref(
          metadataString(metadata, 'client_id') ?? metadataString(metadata, 'clientId'),
        );
      }
      if (metadata?.alert_type === 'SAH_GOVERNMENT_FINANCE_RECOVERY_REQUIRED') {
        return metadataString(metadata, 'resource_url') ?? '/home-care/claims/services-australia';
      }
      if (metadata?.alert_type === 'CLIENT_VISIT_SCHEDULE_CHANGE') {
        return metadataString(metadata, 'resource_url') ?? '/visits';
      }
      if (metadata?.alert_type === 'ZERO_RECIPIENT_FALLBACK') {
        return routeForZeroRecipientFallback(metadata);
      }
      if (metadata?.alert_type === 'ORPHANED_ROUTING_RULES') {
        return '/settings/notification-routing';
      }
      if (sourceTable === 'worker_screenings' || isWorkerScreeningRequest(metadata)) {
        return `/people/compliance/screening/${encoded(sourceId)}`;
      }
      if (isWorkerCertificationAlert(sourceTable, metadata)) {
        const staffId = metadataString(metadata, 'staff_id');
        return staffId
          ? `/people/compliance/certifications/${encoded(staffId)}?focus=${encoded(sourceId)}`
          : `/people/compliance/certifications?focus=${encoded(sourceId)}`;
      }
      if (sourceTable === 'staff') {
        if (metadata?.alert_type === 'SCIM_WORKER_NEEDS_ONBOARDING') {
          return '/people/onboarding?view=not-started';
        }
        return `/people/workers/${encoded(sourceId)}`;
      }
      if (sourceTable === 'compliance_renewal_bookings') {
        const staffId = metadataString(metadata, 'staff_id');
        return staffId
          ? `/people/compliance/certifications/${encoded(staffId)}`
          : `/people/compliance/certifications`;
      }
      return routeForSystemAlert(sourceTable, sourceId, metadata);
    default:
      return `/settings/notification-audit?event=${encoded(sourceId)}`;
  }
}

export function renderNotification(
  workflowType: WorkflowType,
  sourceTable: string,
  sourceId: string,
  ctx: TemplateContext,
): RenderedNotification {
  if (workflowType === 'COMPLIANCE_CAPTURE_URGENT') {
    const whs = sourceTable === 'whs_incidents' && ctx.metadata?.register === 'WHS';
    const care = sourceTable === 'incidents' && ctx.metadata?.register === 'CARE';
    const reference = ctx.metadata?.reference;
    if (
      (!whs && !care) ||
      typeof reference !== 'string' ||
      !(whs ? /^WHS-\d+$/ : /^INC-\d+$/).test(reference)
    ) {
      throw new Error('urgent_capture_invalid_locator');
    }
    const body = whs
      ? `Possibly notifiable WHS incident ${reference} at ${ctx.facilityName ?? 'site not recorded'}. WHS law requires the regulator to be told immediately after the business becomes aware.`
      : `Possible Priority 1 incident ${reference}: provider assessment needed now.`;
    return {
      title: whs ? `Urgent WHS review ${reference}` : `Possible Priority 1 ${reference}`,
      body,
      pushBody: body,
      resourceUrl: whs
        ? `/compliance/whs/incidents/${encoded(sourceId)}`
        : `/compliance/incidents/${encoded(sourceId)}`,
    };
  }
  const rendered = renderNotificationCore(workflowType, sourceTable, sourceId, ctx);

  // Backfill prefix — when the dispatcher is replaying notifications that
  // were stuck in `pending` due to an earlier outage, the migration that
  // releases the backlog stamps `metadata.backfill_audit_note` with
  // recipient-facing context (e.g., "[BACKFILL — SIRS P1 24H DEADLINE
  // MISSED] …"). We prepend that note here so push/email/in_app all carry
  // the marker. The note is also human-readable for the audit_logs row
  // emitted alongside.
  const backfillNote = (ctx.metadata?.backfill_audit_note as string | undefined) ?? null;
  if (backfillNote) {
    return {
      title: rendered.title,
      body: `${backfillNote}\n\n${rendered.body}`,
      resourceUrl: rendered.resourceUrl,
    };
  }
  return rendered;
}

function renderNotificationCore(
  workflowType: WorkflowType,
  sourceTable: string,
  sourceId: string,
  ctx: TemplateContext,
): RenderedNotification {
  const facility = ctx.facilityName ? ` at ${ctx.facilityName}` : '';
  const tierSuffix =
    ctx.escalationTier && ctx.escalationTier > 0 ? ` (escalation tier ${ctx.escalationTier})` : '';
  const deadlineSuffix = ctx.deadlineRemaining ? ` ${ctx.deadlineRemaining} remaining.` : '';
  const sirsPriority = (ctx.metadata?.sirs_priority as number | undefined) ?? null;

  const url = deepLinkFor(workflowType, sourceTable, sourceId, ctx.metadata);

  switch (workflowType) {
    case 'DATA_BREACH':
      return {
        title: 'Data breach review',
        body: 'A data breach needs provider assessment.',
        resourceUrl: url,
      };
    case 'IPC_OUTBREAK':
      return {
        title: 'Outbreak review',
        body: 'A suspected or declared outbreak needs provider review.',
        resourceUrl: url,
      };
    case 'SCREENING_EXPIRY':
      return {
        title: 'Screening expiry',
        body: 'Worker screening evidence is expiring or has expired.',
        resourceUrl: url,
      };
    case 'ESCALATION':
      return {
        title: `Clinical Escalation${tierSuffix}`,
        body: `A new clinical escalation has been raised${facility}. Tap to review.`,
        resourceUrl: url,
      };
    case 'SIRS_SCOPE_REVIEW_REQUIRED':
      return {
        title: 'SIRS Scope Review Required',
        body: `An incident requires reportability review${facility}.${deadlineSuffix}`,
        resourceUrl: url,
      };
    case 'SIRS_INCIDENT':
      return {
        title: `SIRS Incident Reported${tierSuffix}`,
        body:
          sirsPriority !== 1 && sirsPriority !== 2
            ? `An incident requires urgent review; its reportability or priority is still being assessed${facility}.${deadlineSuffix}`
            : ctx.metadata?.provisional === true
              ? `The engine assessed Priority ${sirsPriority}; a staff reportability decision is needed${facility}.${deadlineSuffix}`
              : `A Priority ${sirsPriority} reportable incident requires your attention${facility}.${deadlineSuffix}`,
        resourceUrl: url,
      };
    case 'NON_SIRS_INCIDENT':
      return {
        title: `Incident Report${tierSuffix}`,
        body: `A new incident report has been submitted${facility}.`,
        resourceUrl: url,
      };
    case 'WHS_INCIDENT':
      return {
        title: `WHS Incident${tierSuffix}`,
        body: `A workplace health and safety incident has been reported${facility}.`,
        resourceUrl: url,
      };
    case 'SHIFT_HANDOVER':
      return {
        title: 'Shift Handover',
        body: `A shift handover is ready for your review${facility}.`,
        resourceUrl: url,
      };
    case 'LEAVE_REQUEST':
      return {
        title: 'Leave Request',
        body: leaveRequestNotificationBody(ctx.metadata),
        resourceUrl: url,
      };
    case 'ABSENCE_LEAVE_REQUIRED': {
      const absenceDate =
        metadataText(ctx.metadata, 'absence_date') ??
        metadataText(ctx.metadata, 'absenceDate') ??
        'this shift';
      return {
        title: 'Submit leave to be paid',
        body: `Submit personal leave for ${absenceDate} to be paid`,
        resourceUrl: url,
      };
    }
    case 'LEAVE_EVIDENCE_DUE':
      return {
        title: 'Leave evidence due',
        body: 'Your leave request needs supporting evidence. Open My leave to upload it.',
        resourceUrl: url,
      };
    case 'SHIFT_SWAP_REQUEST':
      return {
        title: 'Shift Swap Request',
        body: 'A shift swap request needs your response.',
        resourceUrl: url,
      };
    case 'TASK_COMPLETION':
      return {
        title: 'Tasks Completed',
        body: 'Care tasks have been completed.',
        resourceUrl: url,
      };
    case 'RESTRICTIVE_PRACTICE_USE':
      return {
        title: `Restrictive Practice Episode${tierSuffix}`,
        body: `A restrictive practice episode has been recorded${facility} and requires clinical review.`,
        resourceUrl: url,
      };
    case 'WOUND_DETERIORATION':
      return {
        title: `Wound Status Change${tierSuffix}`,
        body: `A wound reassessment indicates deterioration${facility}. Clinical review required.`,
        resourceUrl: url,
      };
    case 'CLINICAL_ALERT':
      return {
        title: `Clinical Alert${tierSuffix}`,
        body: `A clinical alert has been raised${facility}.`,
        resourceUrl: url,
      };
    case 'MEDICATION_INCIDENT':
      return {
        title: `Medication Incident${tierSuffix}`,
        body: `A medication incident has been reported${facility}. Clinical review required.`,
        resourceUrl: url,
      };
    case 'OPEN_DISCLOSURE':
      return {
        title: 'Open Disclosure Required',
        body: `A reportable incident requires open disclosure to the affected person${facility}.`,
        resourceUrl: url,
      };
    case 'SIRS_INVESTIGATION_REPORT':
      return {
        title: 'Investigation Report Due',
        body: `A SIRS investigation report is due.${deadlineSuffix}`,
        resourceUrl: url,
      };
    case 'UNEXPLAINED_ABSENCE':
      return {
        title: 'Unexplained Absence',
        body: `An unexplained absence has been reported${facility}. Immediate action required.`,
        resourceUrl: url,
      };
    case 'ROSTER_RUNTIME_SHIFT_NO_SHOW':
      return {
        title: 'Roster no-show — safety and coverage check',
        body: `A rostered shift was flagged as a no-show${facility}. Confirm worker safety, check participant or resident welfare where applicable, and arrange service coverage.`,
        resourceUrl: url,
      };
    case 'ROSTER_RUNTIME_SHIFT_AWAITING_CLOCK':
      return {
        title: 'No clock yet — confirm the visit',
        body: `A Support at Home visit has no clock-in after the usual window${facility}. Confirm attendance, keep chasing, or record that the visit did not happen from Today.`,
        resourceUrl: url,
      };
    case 'ROSTER_RUNTIME_MISSED_CLOCK_OUT':
      return {
        title: 'Missed clock-out — review required',
        body: `A shift was closed automatically after a missed clock-out${facility}. Confirm the actual end time before the timesheet can be approved.`,
        resourceUrl: url,
      };
    case 'TIMESHEET_REJECTED':
      return {
        title: 'Timesheet rejected',
        body: 'One of your timesheets was rejected. Open it to see the reason and what happens next.',
        resourceUrl: url,
      };
    case 'WORKFORCE_WORKED_TIME_PARTIALLY_APPROVED':
      return {
        title: 'Some of your worked time was not approved',
        body: 'Your coordinator approved part of the time outside your rostered hours. Open your timesheet to review the decision or dispute it.',
        resourceUrl: url,
      };
    case 'ESCALATION_EXHAUSTED':
      return {
        title: 'URGENT: Escalation Exhausted',
        body: `An unacknowledged ${(ctx.metadata?.original_workflow_type as string) ?? 'notification'} reached the final escalation tier without acknowledgement.`,
        resourceUrl: url,
      };
    case 'WORKFORCE_COMPLIANCE':
      return renderWorkforceComplianceNotification(sourceTable, sourceId, facility, ctx);
    case 'WORKFORCE_POLICY_ASSIGNED':
      return {
        title: 'Workplace policy assigned',
        body: 'A current workplace policy version is ready for you to read and acknowledge.',
        resourceUrl: url,
      };
    case 'WORKFORCE_POLICY_REMINDER':
      return {
        title: 'Workplace policy acknowledgement reminder',
        body: 'A current workplace policy version is still awaiting your acknowledgement.',
        resourceUrl: url,
      };
    case 'WORKFORCE_REPLACE_ON_SICK_DAY':
      return {
        title: 'Shift Replacement Required',
        body: `A worker is unavailable and the affected shift needs replacement${facility}.`,
        resourceUrl: url,
      };
    case 'RAC_ADMISSION':
      return {
        title: 'Residential Admission Action',
        body: `A residential admission requires review${facility}.`,
        resourceUrl: url,
      };
    case 'RAC_DISCHARGE':
      return {
        title: 'Residential Discharge Action',
        body: `A residential discharge requires review${facility}.`,
        resourceUrl: url,
      };
    case 'RAC_RESPITE':
      return {
        title: 'Residential Respite Action',
        body: `A residential respite workflow requires review${facility}.`,
        resourceUrl: url,
      };
    case 'SAH_ONBOARDING':
      return {
        title: 'Support at Home Onboarding',
        body: 'A Support at Home onboarding action requires review.',
        resourceUrl: url,
      };
    case 'COMPLAINT':
      return {
        title: 'Complaint Review Required',
        body: `A complaint requires review${facility}.`,
        resourceUrl: url,
      };
    case 'CARE_PLAN_REVIEW':
      return {
        title: 'Care Plan Review Required',
        body: 'A care plan is ready or due for review.',
        resourceUrl: url,
      };
    case 'REGULATOR_SUBMISSION_REMINDER':
      return {
        title: 'Regulatory Submission Reminder',
        body: `A regulatory submission requires attention${facility}.`,
        resourceUrl: url,
      };
    // Path-to-GO S3 — NO_SHOW copy is handled above; LATE deliberately omitted.
    case 'SAH_BUDGET_VARIANCE':
      return {
        title: 'Support at Home Budget Variance',
        body: 'Projected spending requires budget review.',
        resourceUrl: url,
      };
    case 'OPS_CARE_MINUTES_SHORTFALL':
      return {
        title: 'Care Minutes Shortfall',
        body: `Care-minute delivery requires review${facility}.`,
        resourceUrl: url,
      };
    case 'OPS_RN_COVERAGE_MID_PERIOD_GAP':
      return {
        title: 'RN Coverage Gap',
        body: `Registered nurse coverage requires immediate review${facility}. This alert is recorded for audit; no in-app recovery page is available yet.`,
        resourceUrl: url,
      };
    case 'OPS_RP_AUTHORISATION_LAPSING':
      return {
        title: 'Restrictive Practice Authorisation Expiring',
        body: `A restrictive practice authorisation requires review${facility}.`,
        resourceUrl: url,
      };
    case 'OPS_RESIDENT_CLINICAL_ALERT':
      return {
        title: 'Resident Clinical Alert',
        body: `A resident clinical alert requires review${facility}.`,
        resourceUrl: url,
      };
    case 'SYSTEM_ALERT':
      if (ctx.metadata?.alert_type === 'PEOPLE_RENEWAL') {
        return {
          title: 'Renewal needs attention',
          body: 'An item is due for renewal or has expired. Open your renewals to review it.',
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.alert_type === 'CHSP_CARE_PLAN_REVIEW_REQUEST_PENDING') {
        return {
          title: 'New CHSP care-plan review request',
          body: 'A client or representative has asked for a care-plan review.',
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.alert_type === 'CHSP_CLIENT_REQUEST_PENDING') {
        return {
          title: 'New CHSP client request',
          body: 'A client request needs coordinator or case manager follow-up.',
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.kind === 'SAH_SERVICE_STRUCTURAL_DRIFT') {
        const driftCount = Number(ctx.metadata?.drift_count ?? 0);
        return {
          title: 'Plan and schedule have drifted',
          body: `${Number.isFinite(driftCount) ? driftCount : 0} scheduled service row(s) no longer match roster re-derivation. This belongs to the coordinator. The monitor does not auto-heal.`,
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.alert_type === 'SAH_GOVERNMENT_FINANCE_RECOVERY_REQUIRED') {
        const commandType = metadataString(ctx.metadata, 'command_type');
        return {
          title: 'Services Australia outcome needs review',
          body: `A ${commandType === 'claim' ? 'claim' : 'invoice'} result is uncertain. Check the agency outcome before taking any further action.`,
          resourceUrl: url,
        };
      }
      // Path-to-GO S18 — published visit change for client portal (in_app only).
      if (ctx.metadata?.alert_type === 'CLIENT_VISIT_SCHEDULE_CHANGE') {
        const titleOverride = metadataString(ctx.metadata, 'title_override');
        const bodyOverride = metadataString(ctx.metadata, 'body_override');
        const portalUrl = metadataString(ctx.metadata, 'resource_url') ?? '/visits';
        return {
          title: titleOverride ?? 'Visit schedule updated',
          body:
            bodyOverride ??
            'A scheduled care visit has changed. Open Visits in the client portal for details.',
          resourceUrl: portalUrl,
        };
      }
      if (ctx.metadata?.alert_type === 'ZERO_RECIPIENT_FALLBACK') {
        return {
          title: 'Notification Routing Review',
          body: 'A critical notification had no matching recipients. Review notification routing.',
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.alert_type === 'ORPHANED_ROUTING_RULES') {
        return {
          title: 'Notification Routing Review',
          body: 'A routing rule references a deactivated user. Review notification routing.',
          resourceUrl: url,
        };
      }
      if (sourceTable === 'roster_shifts' && ctx.metadata?.alert_type === 'SHIFT_UNCOVERED') {
        return {
          title: 'Uncovered Shift',
          body: `A published rostered shift starts within ${UNFILLED_ALERT_HORIZON_HOURS} hours and has no assigned worker${facility}.`,
          resourceUrl: url,
        };
      }
      if (sourceTable === 'open_shifts' || sourceTable === 'shift_offers') {
        return {
          title: 'Open Shift Review',
          body: `An open shift workflow requires review${facility}.`,
          resourceUrl: url,
        };
      }
      if (sourceTable === 'workflow_tasks') {
        return {
          title: 'Workflow Task Escalated',
          body: 'A workflow task requires manager review.',
          resourceUrl: url,
        };
      }
      if (ctx.metadata?.alert_type === 'SCIM_WORKER_NEEDS_ONBOARDING') {
        const workerEmail = metadataString(ctx.metadata, 'worker_email');
        return {
          title: 'SCIM worker needs onboarding',
          body: workerEmail
            ? `SCIM provisioned ${workerEmail} without an onboarding journey. Start onboarding before rostering.`
            : 'A SCIM-provisioned worker needs onboarding before rostering.',
          resourceUrl: url,
        };
      }
      if (isWorkerScreeningRequest(ctx.metadata)) {
        return {
          title: 'Worker Screening Review',
          body: `A worker screening request requires manual verification${facility}.`,
          resourceUrl: url,
        };
      }
      if (isWorkerCertificationAlert(sourceTable, ctx.metadata)) {
        return {
          title: 'Worker Certification Review',
          body: `A worker certification requires compliance review${facility}.`,
          resourceUrl: url,
        };
      }
      return {
        title: 'System Alert',
        body: 'A system alert requires admin review.',
        resourceUrl: url,
      };
    case 'MESSAGE':
      return {
        title: 'New Message',
        body: 'You have received a new message.',
        resourceUrl: url,
      };
    case 'STRANDED_REGULATED_CAPTURE':
      // PHI-FREE by contract. notification_events/deliveries are readable
      // tenant-wide by far broader roles than the coordinator-only quarantine, so
      // this carries NO client name, NO capture type, NO clinical narrative — only
      // that a regulated capture is stranded and needs recovery. The identifying
      // detail lives behind the coordinator-only tables (sub-slice 3 queue).
      // Deliberately NOT in WORKFLOW_DESTINATION_READY (no deep link until that
      // queue ships), so push/email stay suppressed and only the in-app channel
      // delivers — this copy makes that one delivered channel identifiable so the
      // coordinator can tell a stranded capture needs recovery rather than reading
      // the anonymous generic fallback (Codex round-3 P1).
      return {
        title: 'Regulated capture needs recovery',
        body: 'A regulated capture is stranded on a device and needs recovery.',
        resourceUrl: url,
      };
    default:
      return {
        title: 'Statura Care Notification',
        body: 'A workflow event requires your attention.',
        resourceUrl: url,
      };
  }
}

/**
 * Format remaining duration into "X hours Y minutes" or "N days" without
 * exposing the actual deadline timestamp (which could be sensitive context).
 */
export function formatDeadlineRemaining(deadlineIso: string | Date): string {
  const deadline = typeof deadlineIso === 'string' ? new Date(deadlineIso) : deadlineIso;
  const ms = deadline.getTime() - Date.now();
  if (ms <= 0) return 'overdue';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days >= 2) return `${days} days`;
  if (days === 1) return hours === 0 ? '1 day' : `1 day ${hours}h`;
  if (hours >= 1) return minutes === 0 ? `${hours} hours` : `${hours} hours ${minutes} minutes`;
  return `${minutes} minutes`;
}
